import React, { Suspense, lazy } from 'react';
import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { RouterUrl } from './routes';
import { Header } from 'components/Header';
import { Main } from 'components/Main';
import { Footer } from 'components/Footer';
import { SuspenseLoader } from 'components/SuspenseLoader';
import { FullHeightLayout } from 'components/Common/styled-components';
import { useWebitelWidget } from 'hooks';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const AccountLayout = lazy(async () => await import('pages/Account/components/AccountLayout'));
const LoanHistory = lazy(async () => await import('pages/Account/LoanHistory/LoanHistory'));
const Loans = lazy(async () => await import('pages/Account/Loans/Loans'));
const PersonalInformation = lazy(async () => await import('pages/Account/PersonalInformation/PersonalInformation'));
const PaymentMethods = lazy(async () => await import('pages/Account/PaymentMethods/PaymentMethods'));
const CreatePincodePage = lazy(async () => await import('pages/Pincode/CreatePincodePage'));
const Login = lazy(async () => await import('pages/Login/Login'));
const Registration = lazy(async () => await import('pages/Registration/Registration'));
const RegistrationType = lazy(async () => await import('pages/Registration/Step1/RegistrationType'));
const FrontalDNI = lazy(async () => await import('pages/Registration/Step2/FrontalDNI'));
const ReversoDNI = lazy(async () => await import('pages/Registration/Step3/ReversoDNI'));
const PersonalWorkInfo = lazy(async () => await import('pages/Registration/Step4/PersonalWorkInfo'));
const PaymentInfo = lazy(async () => await import('pages/Registration/Step5/PaymentInfo'));
const PhotoFrontDNI = lazy(async () => await import('pages/Registration/Step6/PhotoFrontDNI'));
const PhotoBackDNI = lazy(async () => await import('pages/Registration/Step7/PhotoBackDNI'));
const Selfie = lazy(async () => await import('pages/Registration/Step8/Selfie'));
const CreateApplicationPage = lazy(async () => await import('pages/Registration/Step9/CreateApplicationPage'));
const ApplicationConsideration = lazy(async () => await import('pages/Registration/Step10/ApplicationConsideration'));
const DocumentRequest = lazy(async () => await import('pages/Registration/DocumentRequest/DocumentRequest'));
const SigningAgreement = lazy(async () => await import('pages/Registration/Step11/SigningAgreement'));
const SigningExtensionAgreement = lazy(async () => await import('pages/Registration/Step11/SigningExtensionAgreement'));
const ChoosePaymentMethod = lazy(async () => await import('pages/Payment/PaymentType'));
const OfflinePayment = lazy(async () => await import('pages/Payment/PaymentOffline'));
const OnlinePayment = lazy(async () => await import('pages/Payment/PaymentOnline'));
const QrPayment = lazy(async () => await import('pages/Payment/PaymentQr'));
const PaymentNewCard = lazy(async () => await import('pages/Payment/PaymentNewCard'));
const NotFound = lazy(async () => await import('pages/NotFound/NotFound'));
const BlockedDuplicatedUser = lazy(async () => await import('pages/BlockedDuplicatedUser/BlockedDuplicatedUser'));
const QuickRegistrationFrontDNIPage = lazy(
  async () => await import('pages/Registration/QuickRegistration/QuickRegistrationFrontDNI'),
);
const QuickRegistrationBackDNIPage = lazy(
  async () => await import('pages/Registration/QuickRegistration/QuickRegistrationBackDNI'),
);
const ShortLink = lazy(async () => await import('pages/ShortLink/ShortLink'));
const Reclamation = lazy(async () => await import('pages/Reclamation/Reclamation'));
const AddPaymentCard = lazy(async () => await import('pages/Payment/AddPaymentCard'));
const AddBankAccountPage = lazy(async () => await import('pages/Payment/AddBankAccountPage'));
const PaymentWithAvailCards = lazy(async () => await import('pages/Payment/PaymentWithAvailCards'));

const RouterContent = () => {
  useWebitelWidget();

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <FullHeightLayout>
        <Header />
        <Main>
          <SentryRoutes>
            <Route path={RouterUrl.Home} element={<Navigate to={RouterUrl.Registration} />} />
            <Route path={RouterUrl.User} element={<Navigate to={RouterUrl.Registration} />} />
            <Route path={RouterUrl.Login} element={<Login />} />
            <Route path={RouterUrl.Registration} element={<Registration />} />
            <Route path={RouterUrl.RegistrationType} element={<RegistrationType />} />
            <Route path={RouterUrl.FrontalDNI} element={<FrontalDNI />} />
            <Route path={RouterUrl.ReversoDNI} element={<ReversoDNI />} />
            <Route path={RouterUrl.PersonalWorkInfo} element={<PersonalWorkInfo />} />
            <Route path={RouterUrl.PaymentInfo} element={<PaymentInfo />} />
            <Route path={RouterUrl.PhotoFrontDNI} element={<PhotoFrontDNI />} />
            <Route path={RouterUrl.PhotoBackDNI} element={<PhotoBackDNI />} />
            <Route path={RouterUrl.Selfie} element={<Selfie />} />
            <Route path={RouterUrl.CreateApplicationPage} element={<CreateApplicationPage />} />
            <Route path={RouterUrl.ApplicationConsideration} element={<ApplicationConsideration />} />
            <Route path={RouterUrl.DocumentRequest} element={<DocumentRequest />} />
            <Route path={RouterUrl.SigningAgreement} element={<SigningAgreement />} />
            <Route path={RouterUrl.SigningExtensionAgreement} element={<SigningExtensionAgreement />} />
            <Route path={RouterUrl.ChoosePaymentMethod} element={<ChoosePaymentMethod />} />
            <Route path={RouterUrl.sPayment} element={<OfflinePayment />} />
            <Route path={RouterUrl.bPayment} element={<OnlinePayment />} />
            <Route path={RouterUrl.qPayment} element={<QrPayment />} />
            <Route path={RouterUrl.cPayment} element={<PaymentNewCard />} />
            <Route path={RouterUrl.AddCard} element={<AddPaymentCard />} />
            <Route path={RouterUrl.ChooseCard} element={<PaymentWithAvailCards />} />
            <Route path={RouterUrl.Account} element={<AccountLayout />}>
              <Route path={RouterUrl.Account} element={<Loans />} />
              <Route path={RouterUrl.AccountLoanHistory} element={<LoanHistory />} />
              <Route path={RouterUrl.AccountPersonalInfo} element={<PersonalInformation />} />
              <Route path={RouterUrl.PaymentMethods} element={<PaymentMethods />} />
              <Route path={RouterUrl.AddBankAccount} element={<AddBankAccountPage />} />
            </Route>
            <Route path={RouterUrl.QuickRegistrationFrontDNI} element={<QuickRegistrationFrontDNIPage />} />
            <Route path={RouterUrl.QuickRegistrationBackDNI} element={<QuickRegistrationBackDNIPage />} />
            <Route path={RouterUrl.Reclamation} element={<Reclamation />} />
            <Route path={RouterUrl.ShortLink} element={<ShortLink />} />
            <Route path={RouterUrl.Oops} element={<BlockedDuplicatedUser />} />
            <Route path={RouterUrl.CreatePincode} element={<CreatePincodePage />} />

            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </Main>
        <Footer />
      </FullHeightLayout>
    </Suspense>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

export default Sentry.withProfiler(Router);
